import { useState, useEffect } from "react";
import { api, apiErr } from "@/lib/api";
import { useFetch } from "@/lib/ui";
import { useLang } from "@/context/LangContext";
import { PageHeader, Empty } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Check, X, BellRing, Printer, ClipboardCheck, History } from "lucide-react";

const ST = {
  present: { en: "Present", fr: "Présent", cls: "bg-emerald-100 text-emerald-700" },
  late: { en: "Late", fr: "Retard", cls: "bg-amber-100 text-amber-700" },
  absent: { en: "Absent", fr: "Absent", cls: "bg-rose-100 text-rose-700" },
};

function ReviewQueue() {
  const { L } = useLang();
  const { data: queue, loading, reload } = useFetch(() => api.get("/attendance/review-queue"));
  const [sel, setSel] = useState({});
  const [notify, setNotify] = useState(true);
  const printList = async (onlyAbsentLate) => {
    try {
      const r = await api.raw.get(`/attendance/review-queue/pdf`, { params: { only_absent_late: onlyAbsentLate }, responseType: "blob" });
      const u = URL.createObjectURL(r.data); window.open(u, "_blank"); setTimeout(() => URL.revokeObjectURL(u), 30000);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const rows = queue || [];
  const toggle = (id) => setSel((s) => ({ ...s, [id]: !s[id] }));
  const toggleAll = () => { if (Object.values(sel).filter(Boolean).length === rows.length) setSel({}); else setSel(Object.fromEntries(rows.map((r) => [r.id, true]))); };
  const ids = rows.filter((r) => sel[r.id]).map((r) => r.id);
  const act = async (action) => {
    const use = ids.length ? ids : rows.map((r) => r.id);
    if (use.length === 0) return;
    try {
      const r = await api.post("/attendance/review", { ids: use, action, notify_parents: action === "approve" && notify });
      toast.success(action === "approve" ? L(`Approved ${r.reviewed} · ${r.notified} parent(s) notified`, `Validé ${r.reviewed} · ${r.notified} parent(s) notifié(s)`) : L(`Returned ${r.reviewed} marking(s)`, `${r.reviewed} saisie(s) renvoyée(s)`));
      setSel({}); reload();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  if (loading) return <Empty title={L("Loading…", "Chargement…")} />;
  const groups = {};
  rows.forEach((r) => { const k = `${r.class_name || r.class_id} · ${r.date}`; (groups[k] = groups[k] || []).push(r); });
  return (
    <>
      <div className="flex flex-wrap gap-2 mb-4">
        <Button variant="outline" size="sm" onClick={() => printList(true)} data-testid="att-review-print-absent"><Printer className="h-4 w-4 mr-1" />{L("Print call list (absent/late)", "Imprimer la liste (absents/retards)")}</Button>
        <Button variant="ghost" size="sm" onClick={() => printList(false)} data-testid="att-review-print-all"><Printer className="h-4 w-4 mr-1" />{L("Print all", "Imprimer tout")}</Button>
      </div>
      {rows.length === 0 ? <Empty title={L("Nothing to review", "Rien à valider")} /> : (
        <>
          <div className="flex flex-wrap items-center gap-3 mb-4 bg-card border border-border rounded-lg p-3" data-testid="att-review-bar">
            <Button variant="outline" size="sm" onClick={toggleAll} data-testid="att-review-selectall">{L("Select all", "Tout sélectionner")}</Button>
            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="att-review-notify-label">
              <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} data-testid="att-review-notify" />
              <BellRing className="h-4 w-4 text-sky-600" />{L("Notify parents on approve", "Notifier les parents à la validation")}
            </label>
            <div className="ml-auto flex gap-2">
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => act("approve")} data-testid="att-review-approve"><Check className="h-4 w-4 mr-1" />{L("Approve", "Valider")} {ids.length ? `(${ids.length})` : ""}</Button>
              <Button size="sm" variant="outline" className="text-destructive border-destructive/40" onClick={() => act("reject")} data-testid="att-review-reject"><X className="h-4 w-4 mr-1" />{L("Return", "Renvoyer")}</Button>
            </div>
          </div>
          {notify && <p className="text-xs text-muted-foreground flex items-center gap-1.5 mb-4 -mt-2" data-testid="att-review-notify-hint"><BellRing className="h-3.5 w-3.5 text-sky-600" />{L("Only parents of absent/late students will receive an alert.", "Seuls les parents des élèves absents/en retard recevront une alerte.")}</p>}
          <div className="space-y-5">
            {Object.entries(groups).map(([k, list]) => (
              <div key={k} className="bg-card border border-border rounded-lg overflow-hidden" data-testid="att-review-group">
                <div className="bg-secondary/60 px-4 py-2 text-sm font-semibold flex items-center justify-between"><span>{k}</span><span className="text-xs text-muted-foreground">{L("marked by", "saisi par")} {list[0].marked_by_name || "—"}</span></div>
                <div className="divide-y divide-border">
                  {list.map((r) => (
                    <label key={r.id} className="flex items-center gap-3 px-4 py-2.5 text-sm cursor-pointer hover:bg-secondary/40" data-testid={`att-review-row-${r.id}`}>
                      <input type="checkbox" checked={!!sel[r.id]} onChange={() => toggle(r.id)} data-testid={`att-review-check-${r.id}`} />
                      <span className="flex-1 font-medium">{r.student_name}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${(ST[r.status] || {}).cls || "bg-secondary"}`}>{L((ST[r.status] || {}).en || r.status, (ST[r.status] || {}).fr || r.status)}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}

function HistoryTab() {
  const { L } = useLang();
  const nowMonth = new Date().toISOString().slice(0, 7);
  const [month, setMonth] = useState(nowMonth);
  const [classId, setClassId] = useState("all");
  const [studentId, setStudentId] = useState("all");
  const [statusF, setStatusF] = useState("all");
  const { data: classes } = useFetch(() => api.get("/classes"));
  const [students, setStudents] = useState([]);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (classId === "all") { setStudents([]); setStudentId("all"); return; }
    api.get(`/students`, { params: { class_id: classId } }).then((d) => setStudents(d || [])).catch(() => setStudents([]));
    setStudentId("all");
  }, [classId]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { month };
      if (classId !== "all") params.class_id = classId;
      if (studentId !== "all") params.student_id = studentId;
      if (statusF !== "all") params.status = statusF;
      const d = await api.get(`/attendance/history`, { params });
      setRows(d || []);
      if (studentId !== "all") { const s = await api.get(`/attendance/summary`, { params: { student_id: studentId, month } }); setSummary(s); }
      else setSummary(null);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month, classId, studentId, statusF]);

  const monthlyPdf = async () => {
    if (classId === "all") { toast.error(L("Pick a class first", "Choisissez d'abord une classe")); return; }
    try {
      const r = await api.raw.get(`/attendance/monthly-report/pdf`, { params: { class_id: classId, month }, responseType: "blob" });
      const u = URL.createObjectURL(r.data); window.open(u, "_blank"); setTimeout(() => URL.revokeObjectURL(u), 30000);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <>
      <div className="flex flex-wrap items-end gap-3 mb-4 bg-card border border-border rounded-lg p-3" data-testid="att-history-filters">
        <div><label className="text-xs text-muted-foreground block mb-1">{L("Month", "Mois")}</label><Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40" data-testid="att-history-month" /></div>
        <div><label className="text-xs text-muted-foreground block mb-1">{L("Class", "Classe")}</label>
          <Select value={classId} onValueChange={setClassId}><SelectTrigger className="w-48" data-testid="att-history-class"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">{L("All classes", "Toutes les classes")}</SelectItem>{(classes || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select></div>
        <div><label className="text-xs text-muted-foreground block mb-1">{L("Student", "Élève")}</label>
          <Select value={studentId} onValueChange={setStudentId} disabled={classId === "all"}><SelectTrigger className="w-48" data-testid="att-history-student"><SelectValue placeholder={L("All", "Tous")} /></SelectTrigger>
            <SelectContent><SelectItem value="all">{L("All students", "Tous les élèves")}</SelectItem>{students.map((s) => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name}</SelectItem>)}</SelectContent></Select></div>
        <div><label className="text-xs text-muted-foreground block mb-1">{L("Status", "Statut")}</label>
          <Select value={statusF} onValueChange={setStatusF}><SelectTrigger className="w-36" data-testid="att-history-status"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">{L("All", "Tous")}</SelectItem><SelectItem value="absent">{L("Absent", "Absent")}</SelectItem><SelectItem value="late">{L("Late", "Retard")}</SelectItem><SelectItem value="present">{L("Present", "Présent")}</SelectItem></SelectContent></Select></div>
        <Button variant="outline" size="sm" onClick={monthlyPdf} data-testid="att-history-monthly-pdf"><Printer className="h-4 w-4 mr-1" />{L("Monthly class report (PDF)", "Rapport mensuel de classe (PDF)")}</Button>
      </div>
      {summary && (
        <div className="flex gap-3 mb-4" data-testid="att-history-summary">
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-2 text-sm"><span className="text-muted-foreground">{L("Absences this month", "Absences ce mois-ci")}: </span><b className="text-rose-700" data-testid="att-summary-absences">{summary.absences}</b></div>
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm"><span className="text-muted-foreground">{L("Lates", "Retards")}: </span><b className="text-amber-700" data-testid="att-summary-lates">{summary.lates}</b></div>
        </div>
      )}
      {loading ? <Empty title={L("Loading…", "Chargement…")} /> : rows.length === 0 ? <Empty title={L("No records for these filters", "Aucun enregistrement pour ces filtres")} /> : (
        <div className="bg-card border border-border rounded-lg overflow-hidden" data-testid="att-history-table">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60 text-xs uppercase text-muted-foreground"><tr><th className="text-left px-4 py-2">{L("Date", "Date")}</th><th className="text-left px-4 py-2">{L("Student", "Élève")}</th><th className="text-left px-4 py-2">{L("Class", "Classe")}</th><th className="text-left px-4 py-2">{L("Status", "Statut")}</th><th className="text-left px-4 py-2">{L("Review", "Validation")}</th></tr></thead>
            <tbody className="divide-y divide-border">
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-secondary/30" data-testid={`att-history-row-${r.id}`}>
                  <td className="px-4 py-2 whitespace-nowrap">{r.date}</td>
                  <td className="px-4 py-2 font-medium">{r.student_name}</td>
                  <td className="px-4 py-2">{r.class_name || "—"}</td>
                  <td className="px-4 py-2"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${(ST[r.status] || {}).cls || "bg-secondary"}`}>{L((ST[r.status] || {}).en || r.status, (ST[r.status] || {}).fr || r.status)}</span></td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">{r.review_status === "approved" ? L("Approved", "Validé") : r.review_status === "rejected" ? L("Returned", "Renvoyé") : r.review_status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

export default function AttendanceReview() {
  const { L } = useLang();
  const [tab, setTab] = useState("queue");
  return (
    <div className="animate-fade-up">
      <PageHeader title={L("Attendance Review", "Validation des présences")}
        description={L("Review teacher markings and browse the permanent attendance history.", "Validez les saisies des enseignants et consultez l'historique permanent des présences.")} />
      <div className="flex gap-2 mb-5">
        <Button variant={tab === "queue" ? "default" : "outline"} size="sm" onClick={() => setTab("queue")} data-testid="att-tab-queue"><ClipboardCheck className="h-4 w-4 mr-1" />{L("To review", "À valider")}</Button>
        <Button variant={tab === "history" ? "default" : "outline"} size="sm" onClick={() => setTab("history")} data-testid="att-tab-history"><History className="h-4 w-4 mr-1" />{L("History", "Historique")}</Button>
      </div>
      {tab === "queue" ? <ReviewQueue /> : <HistoryTab />}
    </div>
  );
}
